import type { AppState } from '../types'
import { initialState } from '../data/catalog'

const DB_NAME = 'thrivecove'
const STORE_NAME = 'state'
const LEGACY_STATE_KEY = 'app-state-v1'
const MIGRATION_OWNER_KEY = 'legacy-state-migrated-to-user'

function stateKey(userId: string) {
  return `app-state-v2:${userId}`
}

function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, 1)
    request.onupgradeneeded = () => {
      const database = request.result
      if (!database.objectStoreNames.contains(STORE_NAME)) database.createObjectStore(STORE_NAME)
    }
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error)
  })
}

function mergeState(candidate: Partial<AppState>): AppState {
  return {
    ...initialState,
    ...candidate,
    inventory: candidate.inventory ?? initialState.inventory,
    fish: candidate.fish ?? [],
    placements: candidate.placements ?? initialState.placements,
    sessions: candidate.sessions ?? [],
    pendingTaskLogs: candidate.pendingTaskLogs ?? [],
    settings: { ...initialState.settings, ...(candidate.settings ?? {}) },
  }
}

async function readIndexedDb(key: string): Promise<AppState | undefined> {
  const database = await openDatabase()
  try {
    return await new Promise<AppState | undefined>((resolve, reject) => {
      const transaction = database.transaction(STORE_NAME, 'readonly')
      const request = transaction.objectStore(STORE_NAME).get(key)
      request.onsuccess = () => resolve(request.result as AppState | undefined)
      request.onerror = () => reject(request.error)
    })
  } finally {
    database.close()
  }
}

export async function loadState(userId: string): Promise<AppState> {
  const key = stateKey(userId)

  try {
    const value = await readIndexedDb(key)
    if (value) return mergeState(value)
  } catch {
    // Fall through to localStorage.
  }

  const local = localStorage.getItem(key)
  if (local) return mergeState(JSON.parse(local) as Partial<AppState>)

  // One-time migration of pre-account ThriveCove data into the first account
  // used on this browser. This prevents a second account inheriting it.
  if (!localStorage.getItem(MIGRATION_OWNER_KEY)) {
    try {
      const legacyIndexed = await readIndexedDb(LEGACY_STATE_KEY)
      const legacyLocal = localStorage.getItem(LEGACY_STATE_KEY)
      const legacy = legacyIndexed ?? (legacyLocal ? JSON.parse(legacyLocal) as Partial<AppState> : undefined)
      if (legacy) {
        localStorage.setItem(MIGRATION_OWNER_KEY, userId)
        return mergeState(legacy)
      }
    } catch {
      const legacyLocal = localStorage.getItem(LEGACY_STATE_KEY)
      if (legacyLocal) {
        localStorage.setItem(MIGRATION_OWNER_KEY, userId)
        return mergeState(JSON.parse(legacyLocal) as Partial<AppState>)
      }
    }
  }

  return mergeState({})
}

export async function saveState(userId: string, state: AppState): Promise<void> {
  const key = stateKey(userId)
  localStorage.setItem(key, JSON.stringify(state))
  try {
    const database = await openDatabase()
    await new Promise<void>((resolve, reject) => {
      const transaction = database.transaction(STORE_NAME, 'readwrite')
      transaction.objectStore(STORE_NAME).put(state, key)
      transaction.oncomplete = () => resolve()
      transaction.onerror = () => reject(transaction.error)
    })
    database.close()
  } catch {
    // localStorage is the deliberate fallback for browsers that block IndexedDB.
  }
}

export async function clearState(userId: string): Promise<void> {
  const key = stateKey(userId)
  localStorage.removeItem(key)
  try {
    const database = await openDatabase()
    await new Promise<void>((resolve, reject) => {
      const transaction = database.transaction(STORE_NAME, 'readwrite')
      transaction.objectStore(STORE_NAME).delete(key)
      transaction.oncomplete = () => resolve()
      transaction.onerror = () => reject(transaction.error)
    })
    database.close()
  } catch {
    // Nothing else to clear.
  }
}
